import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import NetworkingCalc from '../components/calculators/NetworkingCalc'
import { Breadcrumbs } from '../components/shared/Breadcrumbs'
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper'
import { generateBreadcrumbList } from '../utils/schemaHelpers'

export default function NetworkingPage() {
  return (
    <>
      <Helmet>
        <title>Azure Networking Cost Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure networking costs for UK South — NAT Gateway, VNet peering, Public IPs, DDoS Protection. NAT Gateway costs ~£33/month idle." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/networking" />
        <meta property="og:title" content="Azure Networking Cost Calculator UK" />
        <meta property="og:description" content="Free independent Azure Networking pricing calculator for UK South. NAT Gateway, VNet peering, Public IPs, DDoS Protection." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/networking" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([
          { name: 'Home', url: '/', position: 1 },
          { name: 'Azure Networking Calculator', url: '/networking', position: 2 },
        ]))}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Azure Networking Cost Calculator',
          description: 'Independent Azure Networking pricing calculator for UK South',
          url: 'https://www.azure-calc.co.uk/networking',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        })}</script>
      </Helmet>

      <div className="px-6 py-6 w-full">
        <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
          <Breadcrumbs />
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Azure Networking Cost Calculator
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            UK South pricing · VNet peering · NAT Gateway · Public IP · DDoS Protection
          </p>
        </div>

        <NetworkingCalc />

        <CalculatorContentWrapper
          eyebrow="Pricing Guide"
          title="Azure networking has persistent, often-forgotten costs"
          intro={
            <>
              <p className="mb-3">
                Networking charges in Azure feel invisible until they're not. A VM in a subnet has no explicit
                network charge. But the moment you add a NAT Gateway, public IP, or inter-region failover, new
                charges appear. Unlike compute (which you deallocate) or storage (which scales with usage),
                networking resources often have standing charges and can't simply be paused. A NAT Gateway left
                idle still costs £33/month. A public IP address with zero inbound traffic still costs. Teams
                often overlook these because they're small in isolation but material across dozens of resources.
              </p>
              <p>
                This page keeps the calculator as the primary action, then explains the hidden cost patterns
                that most architects don't budget for until it's in the invoice.
              </p>
            </>
          }
          sections={[
            {
              id: 'networking-components',
              title: 'Azure networking components and their cost drivers',
              body: (
                <>
                  <p className="mb-3">
                    Azure networking is modular, and each component has its own billing profile:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Virtual Network (VNet):</strong> The VNet itself is free. You pay for resources _in_ it.</li>
                    <li><strong>Public IP Address:</strong> £0.0128/hour (idle) or £0.0064/hour (in-use). The "in-use" rate applies when the IP is actively receiving traffic.</li>
                    <li><strong>NAT Gateway:</strong> £33/month per gateway + £0.18 per million processed bytes. No pause mode; billing only stops on deletion.</li>
                    <li><strong>Virtual Network Peering:</strong> Data passing through peer links is charged (~£0.016/GB); VNets in the same region are cheaper than cross-region.</li>
                    <li><strong>Load Balancer:</strong> ~£0.21/hour (Standard + Basic pricing varies slightly). High-traffic scenarios add rule and data processing charges.</li>
                    <li><strong>Application Gateway:</strong> ~£0.25/hour + capacity unit charges. Often forgotten in cost plans because it's regional.</li>
                    <li><strong>Express Route / VPN Gateway:</strong> Standing monthly charges regardless of traffic volume.</li>
                  </ul>
                </>
              ),
            },
            {
              id: 'nat-gateway-trap',
              title: 'The NAT Gateway trap: standing charge with no deallocate',
              body: (
                <>
                  <p className="mb-3">
                    NAT Gateway is perhaps the most deceptive networking cost because it has no pause state. Unlike
                    a Virtual Machine (which you deallocate to stop charges), a NAT Gateway charges a standing fee
                    of ~£33/month simply for existing. If you provision a NAT Gateway for a dev or test environment
                    and "pause" it by removing traffic, the charge continues until you explicitly delete the resource.
                  </p>
                  <p className="mb-3">
                    Teams often discover this months later: they find an idle NAT Gateway in a dev subscription and
                    realize it's been charging £33/month for three months = £99 wasted. Multiply across all subscriptions
                    and environments, and NAT Gateway becomes a material cost-avoidance target.
                  </p>
                  <p>
                    <strong>Best practice:</strong> Use NAT Gateway only in production or long-running environments. For
                    dev/test workloads, use a load balancer's SNAT capability or managed outbound rules on NSGs to avoid
                    the standing charge entirely.
                  </p>
                </>
              ),
            },
            {
              id: 'public-ip-idle-cost',
              title: 'Public IP addresses never pause—they cost even when idle',
              body: (
                <>
                  <p className="mb-3">
                    A public IP address in Azure costs £0.0128/hour if idle (unused), or £0.0064/hour if in-use. "In-use"
                    typically means it's associated with a running VM, load balancer, or Application Gateway. But the
                    distinction is often invisible to engineers: allocate a public IP for a temporary VM, delete the VM,
                    and forget to delete the public IP. The charge persists.
                  </p>
                  <p className="mb-3">
                    Over a year, a single idle public IP costs ~£112. Across a large infrastructure with orphaned IPs,
                    this becomes significant. Many teams run quarterly audits to find and delete orphaned public IPs.
                  </p>
                  <p>
                    <strong>Best practice:</strong> Use lifecycle policies and tagging to track public IP age. Delete IPs
                    not actively used for inbound access. For outbound-only traffic, prefer NAT Gateway (if the cost is
                    justified) or managed outbound rules to avoid public IP charges entirely.
                  </p>
                </>
              ),
            },
            {
              id: 'vnet-peering-costs',
              title: 'VNet peering: cheap within-region, expensive cross-region',
              body: (
                <>
                  <p className="mb-3">
                    VNet peering allows two virtual networks to communicate directly. Intra-region peering is relatively
                    cheap (~£0.0048/GB GBP-equivalent), but cross-region peering is more expensive (~£0.016/GB). The
                    difference reflects Microsoft's backbone costs: same-region traffic stays within a local cluster,
                    while cross-region uses expensive inter-datacenter bandwidth.
                  </p>
                  <p className="mb-3">
                    For architectures that hub-and-spoke multi-region, every byte of spoke-to-spoke traffic routes through
                    the hub and incurs two peering charges (spoke-to-hub + hub-to-spoke). High-traffic cross-region spoke
                    communication can become a material cost driver.
                  </p>
                  <p>
                    <strong>Best practice:</strong> Keep high-traffic workloads on the same region where feasible. For
                    multi-region, consider whether fully meshed peering or a transit hub is cost-effective compared to
                    running replicas closer to traffic sources.
                  </p>
                </>
              ),
            },
            {
              id: 'load-balancer-vs-app-gateway',
              title: 'Load Balancer vs Application Gateway: cost and use-case trade-offs',
              body: (
                <>
                  <p className="mb-3">
                    Both components can distribute inbound traffic, but they're priced and intended for different layers:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li><strong>Load Balancer (Layer 4):</strong> ~£0.21/hour. Low per-connection overhead. Good for
                      non-HTTP, high-throughput scenarios (databases, caching, raw TCP/UDP).</li>
                    <li><strong>Application Gateway (Layer 7):</strong> ~£0.25/hour + capacity units. Inspects HTTP headers,
                      routes based on paths/hostnames, terminates TLS. Higher per-request cost but richer routing.</li>
                  </ul>
                  <p>
                    The choice isn't arbitrary: choosing the "wrong" tool for the scenario can inflate costs. A simple
                    TCP load-balancer workload using Application Gateway pays Layer 7 pricing for Layer 4 behavior. Conversely,
                    HTTP routing with a Layer 4 load balancer requires hacks and wastes throughput.
                  </p>
                  <p>
                    <strong>Best practice:</strong> Match the component to the layer. Also be aware of Azure Front Door
                    (global load balancing) and Traffic Manager (DNS-based routing) for multi-region scenarios; both have
                    different price models and may be more suitable than Application Gateway in some cases.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked example: hybrid architecture with hidden networking costs',
              body: (
                <>
                  <p className="mb-3">
                    A company runs a hybrid architecture:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li>Primary workload: UK South (VMs, App Service)</li>
                    <li>On-premises database: connected via Express Route</li>
                    <li>Disaster recovery: replicated to North Europe VNets</li>
                    <li>NAT Gateway: for outbound SaaS integrations</li>
                    <li>Load Balancer: distributes inbound traffic to VMs</li>
                    <li>3 Public IPs: for remote access and API endpoints</li>
                  </ul>
                  <p className="mb-2">
                    Monthly networking cost breakdown (estimate):
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>NAT Gateway: £33.00</li>
                    <li>Load Balancer: ~£155 (£0.21/hour × 730 hours)</li>
                    <li>Public IPs (3 idle): ~£28 (£0.0128/hour × 730 hours × 3)</li>
                    <li>Express Route: £150–300 (typical peering cost)</li>
                    <li>VNet peering (cross-region replication traffic): ~£200–500 (depending on DR sync volume)</li>
                    <li><strong>Total:</strong> ~£566–1,026/month just for networking infrastructure</li>
                  </ul>
                  <p>
                    Most teams don't budget per-component before deployment. They budget by service (e.g., "VMs cost £X"),
                    then discover that networking is 30% of the total bill due to load balancer + peering + NAT Gateway
                    overhead.
                  </p>
                </>
              ),
            },
            {
              id: 'faq',
              title: 'Common questions',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Can I deallocate a NAT Gateway to stop charges?</strong> No. NAT Gateway has no deallocate state.
                    The only way to stop charges is to delete it entirely. This is a key source of orphaned resources and
                    unnecessary costs.
                  </p>
                  <p className="mb-3">
                    <strong>Are inbound charges included in the bandwidth calculation?</strong> No. Inbound data transfer to
                    Azure is free. Only outbound (egress) is charged, which is handled separately in the Bandwidth calculator.
                  </p>
                  <p className="mb-3">
                    <strong>Do I pay for VNet peering if no traffic crosses it?</strong> No. Peering is only charged per GB
                    transferred. If two VNets are peered but no traffic crosses, there's no charge. This is different from
                    resources like NAT Gateway, which have standing charges.
                  </p>
                  <p>
                    <strong>Is VNet-to-VNet traffic within the same subscription cheaper?</strong> No. Peering charges are
                    the same regardless of subscription or tenant. Region (same vs cross) is what matters.
                  </p>
                </>
              ),
            },
          ]}
          methodologyNote={
            <>
              <p className="mb-2">
                Rates are sourced from the Azure Retail Prices API for UK South in GBP. The calculator includes standing
                charges for resources like NAT Gateway and public IPs, plus per-GB costs for peering and data transfer.
                Actual costs depend on resource configuration, traffic volume, and region selection.
              </p>
              <Link to="/methodology" className="text-[#0078d4] hover:underline">
                How prices are calculated →
              </Link>
            </>
          }
          relatedLinks={[
            { label: 'Bandwidth / Egress Calculator', to: '/bandwidth' },
            { label: 'Virtual Machines Calculator', to: '/virtual-machines' },
            { label: 'App Service Calculator', to: '/app-service' },
            { label: 'Home', to: '/' },
          ]}
        />
      </div>
    </>
  )
}
